import React, { useMemo } from "react";
import styles from "./DeleteItemDialog.module.css";
import { DeleteDialogController } from "../../../hooks/useDeleteDialog";
import Modal from "../../overlay/Modal/Modal";
import { Button } from "../../controls/Button/Button";
type Props = {
  deleteModal: DeleteDialogController;

  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * The two destructive choices of the series variant, used only when the controller was opened
   * with `isSeries`. They carry the noun ("Nur diesen Termin"), which is why they are props.
   */
  singleLabel?: string;
  seriesLabel?: string;
};

const DeleteItemDialog: React.FC<Props> = ({
  deleteModal,
  confirmLabel = "Jetzt löschen",
  cancelLabel = "Zurück",
  singleLabel = "Nur diesen Eintrag",
  seriesLabel = "Diesen und alle folgenden",
}) => {
  const {
    isOpen,
    title,
    description,
    tone,
    isSeries,
    error,
    isLoading,
    pendingScope,
    closeDelete,
    confirmDelete,
  } = deleteModal;

  const canClose = !isLoading;

  const titleNode = useMemo(() => {
    return <p className="popUpTitle">{title}</p>;
  }, [title]);

  const handleCancel = () => {
    if (!canClose) return;
    closeDelete();
  };

  const handleConfirm = async (forFullSeries = false) => {
    if (isLoading) return;
    await confirmDelete({ forFullSeries });
  };

  return (
    <Modal
      empty
      // `empty` skips the header, so the dialog has no title to be labelled by.
      ariaLabel={title}
      className={styles.popUp}
      show={isOpen}
      setShow={(val) => {
        if (!val) handleCancel();
      }}
    >
      {/* icon */}
      <svg
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <rect width="64" height="64" rx="32" fill="#E53012" />
        <path
          d="M42.0612 39.938C42.343 40.2198 42.5013 40.602 42.5013 41.0005C42.5013 41.399 42.343 41.7812 42.0612 42.063C41.7794 42.3448 41.3972 42.5031 40.9987 42.5031C40.6002 42.5031 40.218 42.3448 39.9362 42.063L31.9999 34.1242L24.0612 42.0605C23.7794 42.3423 23.3972 42.5006 22.9987 42.5006C22.6002 42.5006 22.218 42.3423 21.9362 42.0605C21.6544 41.7787 21.4961 41.3965 21.4961 40.998C21.4961 40.5995 21.6544 40.2173 21.9362 39.9355L29.8749 31.9992L21.9387 24.0605C21.6569 23.7787 21.4986 23.3965 21.4986 22.998C21.4986 22.5995 21.6569 22.2173 21.9387 21.9355C22.2205 21.6537 22.6027 21.4954 23.0012 21.4954C23.3997 21.4954 23.7819 21.6537 24.0637 21.9355L31.9999 29.8742L39.9387 21.9342C40.2205 21.6524 40.6027 21.4941 41.0012 21.4941C41.3997 21.4941 41.7819 21.6524 42.0637 21.9342C42.3455 22.216 42.5038 22.5982 42.5038 22.9967C42.5038 23.3953 42.3455 23.7775 42.0637 24.0592L34.1249 31.9992L42.0612 39.938Z"
          fill="white"
        />
      </svg>

      {titleNode}

      {description && (
        <p className={`${styles.description} ${tone === "warning" ? styles.descriptionWarning : ""}`}>
          {description}
        </p>
      )}

      {error && (
        <div className="text-[#FB2424] text-center" role="alert" aria-live="polite">
          {error}
        </div>
      )}

      <div className={styles.buttomRow}>
        {isSeries ? (
          <>
            <Button
              onClick={() => handleConfirm(false)}
              disabled={isLoading}
              className={`${styles.actionButton} ${styles.scopeButton}`}
              type="button"
            >
              {pendingScope === "single" ? "Löschen..." : singleLabel}
            </Button>

            <Button
              onClick={() => handleConfirm(true)}
              disabled={isLoading}
              className={`${styles.actionButton} ${styles.scopeButton}`}
              type="button"
            >
              {pendingScope === "series" ? "Löschen..." : seriesLabel}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => handleConfirm(false)}
            disabled={isLoading}
            className={styles.actionButton}
            type="button"
          >
            {isLoading ? "Löschen..." : confirmLabel}
          </Button>
        )}

        <Button
          onClick={handleCancel}
          disabled={isLoading}
          className={styles.backButton}
          type="button"
        >
          {cancelLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default DeleteItemDialog;
